import { ReactNode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getLangDirection } from "../../../config/i18n";
import {
  getStoredPublicAppLang,
  getStoredRoomLangFromUser,
  normalizeLanguage,
  parseSupportedLanguage,
  storeRoomLang,
} from "../utils/langUtils";
import {
  getPageContext,
  resolveLangSync,
  type PageContext,
} from "../utils/resolveLangSync";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import type { ShellRoomState } from "../shellRoom";
import { setRoomContentLang } from "../stores/roomContentLangStore";

export function LangSync({
  shellRoom,
  children,
}: {
  shellRoom: ShellRoomState;
  children: ReactNode;
}) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { currentUser } = useCurrentUser();
  const currentRoom = shellRoom.kind === "loaded" ? shellRoom.room : null;
  const pageContext = getPageContext(location.pathname, currentRoom);

  const activeLang = normalizeLanguage(
    i18n.resolvedLanguage !== undefined
      ? i18n.resolvedLanguage
      : navigator.language
  );

  // Track the last context in which we resolved, so we don't re-resolve
  // on the same page when currentUser ref changes (which causes flicker
  // during Settings save when the PATCH is in flight). roomId is included
  // so room-to-room navigation triggers a fresh resolution.
  const lastResolveContext = useRef<{
    pageKind: PageContext["kind"];
    roomId: string | null;
    userLoaded: boolean;
  } | null>(null);

  const checkActiveLang = useCallback(async () => {
    const currentLang = normalizeLanguage(
      i18n.resolvedLanguage !== undefined
        ? i18n.resolvedLanguage
        : navigator.language
    );

    // Explicit override via `?lang=<code>` — forces BOTH the app (i18n) and the
    // room content language, bypassing the normal user/room resolution. Used by
    // the screenshot pipeline to capture a room in a specific language, and as a
    // general deep-link override. Invalid codes fall through to normal resolution.
    const langParam = new URLSearchParams(location.search).get("lang");
    const forcedLang =
      langParam !== null ? parseSupportedLanguage(langParam) : undefined;
    if (forcedLang !== undefined) {
      // `?lang=` is a deliberate override (deep link, screenshot pipeline), so it
      // relabels room text as an explicit choice would.
      setRoomContentLang({ language: forcedLang, source: "explicit" });
      if (forcedLang !== currentLang) {
        void i18n.changeLanguage(forcedLang);
      }
      return;
    }

    const userLoaded = currentUser !== null;
    const roomId = currentRoom !== null ? currentRoom.id : null;
    const contextKey = { pageKind: pageContext.kind, roomId, userLoaded };
    const lastCtx = lastResolveContext.current;

    // Skip re-resolution when same page type, room, and user-loaded status.
    // This prevents flicker when currentUser ref changes (e.g. refetch
    // after query key change) while we're still on the same page.
    if (
      lastCtx !== null &&
      lastCtx.pageKind === contextKey.pageKind &&
      lastCtx.roomId === contextKey.roomId &&
      lastCtx.userLoaded === contextKey.userLoaded
    ) {
      return;
    }

    const navigatorLang = parseSupportedLanguage(
      navigator.language.slice(0, 2)
    );

    let roomOverride: ReturnType<typeof getStoredPublicAppLang> = null;
    if (
      pageContext.kind === "room" &&
      currentUser !== null &&
      currentRoom !== null
    ) {
      roomOverride = await getStoredRoomLangFromUser(
        currentRoom.id,
        currentUser
      );
    }

    const userLanguage = currentUser !== null ? currentUser.language : null;

    const resolved = resolveLangSync({
      pageContext,
      userLanguage,
      storedPublicAppLang: getStoredPublicAppLang(),
      navigatorLang: navigatorLang !== undefined ? navigatorLang : null,
      roomOverride,
    });

    if (resolved === null) return;

    lastResolveContext.current = contextKey;

    if (pageContext.kind === "room" && resolved !== userLanguage) {
      // `resolved` differs from the user own language for exactly two reasons, and
      // they are not interchangeable for room TEXT: a persisted per-room override is
      // an explicit choice, whereas `allowedLanguages[0]` is admin tag order. The
      // discriminator is recorded HERE, where `roomOverride` is still in scope — it
      // cannot be recovered from the bare language downstream.
      //
      // ⚠️ THIRD-BRANCH RISK. This DERIVES the source by comparison rather than having
      // `resolveRoomLanguage` report which branch it took. Correct for today's two
      // branches, including a stale override no longer in `allowedLanguages` (it
      // compares false and lands on "resolved", which is right).
      //
      // But if anyone adds a THIRD branch to `resolveRoomLanguage` — an org-level or
      // config default, say — this comparison labels it "resolved" SILENTLY and nobody
      // is forced to decide whether that is correct. If you add a branch, decide here
      // too. Reporting the source from `resolveRoomLanguage` would make that a
      // compile-time decision instead; it was left out because the value would have to
      // be threaded through `resolveLangSync`, widening its return across four page
      // contexts (three of which have no natural source) plus 45 assertions in
      // resolveLangSync.test.ts. Worth revisiting if a third branch ever lands.
      setRoomContentLang({
        language: resolved,
        source: resolved === roomOverride ? "explicit" : "resolved",
      });
      if (userLanguage !== null && userLanguage !== currentLang) {
        void i18n.changeLanguage(userLanguage);
      }
    } else {
      setRoomContentLang(null);
      if (resolved !== currentLang) {
        void i18n.changeLanguage(resolved);
      }
    }
  }, [pageContext, currentRoom, currentUser, i18n, location.search]);

  useEffect(() => {
    if (shellRoom.kind === "loading") return;
    void checkActiveLang();
  }, [shellRoom.kind, checkActiveLang]);

  useEffect(() => {
    if (shellRoom.kind === "loading") return;
    if (
      currentRoom !== null &&
      currentUser !== null &&
      currentRoom.allowedLanguages.includes(activeLang)
    ) {
      storeRoomLang(currentRoom.id, activeLang);
    }

    document.dir = getLangDirection(activeLang);
    document.documentElement.lang = activeLang;
  }, [shellRoom.kind, activeLang, currentRoom, currentUser]);

  return <>{children}</>;
}
